import React from 'react';
import { Check, Tag, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useTheme } from '@/contexts/ThemeContext';
import AnimatedBorderBadge from '@/components/editorial/AnimatedBorderBadge';
import { regionConfig } from '@/config/region';

const EditorialPricingSummary = React.forwardRef<HTMLElement>((_, ref) => {
  const { theme } = useTheme();

  const headingColor = theme === 'dark' ? '#FFFFFF' : '#111111';
  const textColor = theme === 'dark' ? '#AAAAAA' : '#555555';

  const benefits = [
    "All the Tools to Capture More Leads",
    "Nurture & Close Leads into Customers Automatically",
    "Native AI Tools",
    "Full Online Booking, Lead Campaigns, Pipelines, Social Planner, Website Builder, and More!",
    "Unlimited Contacts & Users, Add as Many Contacts & Users as You Need!"
  ];

  return (
    <section ref={ref} className="py-16 transition-colors duration-300">
      <div className="max-w-[960px] mx-auto px-[7%] sm:px-6 lg:px-8">
        {/* Pricing Badge */}
        <AnimatedBorderBadge text="Pricing" icon={Tag} />

        {/* Section Header */}
        <div className="text-center mb-8">
          <h2 className="text-3xl lg:text-4xl font-bold mb-4 tracking-tight" style={{ color: headingColor }}>
            Scale Your Business. Not Your Costs.
          </h2>
          <p className="text-sm" style={{ color: textColor }}>
            Choose the plan that suits. Start for free.
          </p>
        </div>

        {/* Price Callout */}
        <div className="text-center mb-10">
          <div className="inline-flex items-baseline gap-1">
            <span className="text-sm" style={{ color: textColor }}>from</span>
            <span className="text-4xl lg:text-5xl font-bold" style={{ color: headingColor }}>{regionConfig.currency}{regionConfig.pricing.minimums}</span>
            <span className="text-sm" style={{ color: textColor }}>/ month</span>
          </div>
        </div>

        {/* Benefits List */}
        <div className="max-w-xl mx-auto mb-10">
          <ul className="space-y-4">
            {benefits.map((benefit, index) => (
              <li key={index} className="flex items-start gap-3">
                <div 
                  className="w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5"
                  style={{ backgroundColor: theme === 'dark' ? '#FFFFFF' : '#111111' }}
                >
                  <Check 
                    className="w-3 h-3" 
                    strokeWidth={3} 
                    style={{ color: theme === 'dark' ? '#000000' : '#FFFFFF' }}
                  />
                </div>
                <span className="text-sm leading-relaxed" style={{ color: textColor }}>{benefit}</span>
              </li>
            ))}
          </ul>
        </div>

        {/* CTA Button */}
        <div className="flex justify-center">
          <Link
            to="/pricing#pricing-section"
            className="inline-flex items-center gap-2 px-6 py-3 rounded-lg text-xs font-semibold transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]"
            style={{
              backgroundColor: theme === 'dark' ? '#FFFFFF' : '#111111',
              color: theme === 'dark' ? '#000000' : '#FFFFFF'
            }}
          >
            See Full Pricing
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
});

EditorialPricingSummary.displayName = 'EditorialPricingSummary';

export default EditorialPricingSummary;
